import React, { useCallback } from 'react';

import { useFlag } from '@modernloop/shared/feature-flag';

import Drawer from 'src/components/Drawer';

import { useTaskTimelineVisibility } from 'src/contexts/TaskTimelineVisibilityProvider';

import { SidePanelType } from 'src/hooks/useUrlSearchParams';

import DiscardNoteConfirmationDialog from 'src/views-new/sidepanel/common/DiscardNoteConfirmationDialog';

import AIAssistantSidepanel from './AIAssistant';
import AiSchedulingSidepanel from './AiScheduling';
import InterviewModuleSidepanel from './InterviewModuleMember';
import ScheduleTaskSidePanel from './ScheduleTask';
import { SidePanelManager, SidePanelProvider, useSidePanelState } from './common/SidePanelManager';
import { useSidePanelUrlData } from './common/useSidePanelUrlData';

const SCHEDULE_TASK_DRAWER_WIDTH = 500;
const TASK_TIMELINE_DRAWER_WIDTH = 1000;

/**
 * SidePanel is responsible for showing details for Schedule task.
 * This component can be extended to add support for more items which need to be opened in side panel.
 * For e.g. we can show activity log for a task or candidate in side panel.
 *
 * Note: Do not use this component to show details in side panel when the data shown is tightly coupled with the current page.
 * E.g. When showing activity log for settings page, we should not use this component.
 *      Instead show the Drawer component in the settings page itself.
 */
const SidePanelBase = () => {
  const sidePanelState = useSidePanelState();
  const sidePanelUrlData = useSidePanelUrlData();

  const sidePanelData = sidePanelUrlData || sidePanelState;

  const hideSidePanelClose = useCallback(() => {
    SidePanelManager.closeSidePanel();
  }, []);

  const timelineViewEnabled = useFlag('TASK_TWO_COLUMN_TIMELINE_VIEW');
  const { resolved, timelineVisible } = useTaskTimelineVisibility();
  // Widen the drawer for ScheduleTask split-panel only while both columns are shown; a hidden
  // timeline leaves the details column alone, so the drawer gives the width back. This must gate on
  // `resolved` exactly as ScheduleTask does, or a user who hid the timeline sees the drawer open
  // wide and then snap narrow once the stored preference arrives.
  const showTaskTimeline =
    timelineViewEnabled && resolved && timelineVisible && sidePanelData?.data?.type === SidePanelType.ScheduleTask;
  const drawerWidth =
    sidePanelData?.data?.type === SidePanelType.ScheduleTask
      ? showTaskTimeline
        ? TASK_TIMELINE_DRAWER_WIDTH
        : SCHEDULE_TASK_DRAWER_WIDTH
      : undefined;

  let jsx = <></>;

  if (sidePanelData?.data?.type === SidePanelType.ScheduleTask) {
    jsx = (
      <ScheduleTaskSidePanel
        scheduleTaskId={sidePanelData.data.scheduleTaskId}
        showRequirements={sidePanelData.data.showRequirements}
      />
    );
  } else if (sidePanelData?.data?.type === SidePanelType.InterviewModuleMember) {
    jsx = (
      <InterviewModuleSidepanel
        moduleMemberId={sidePanelData.data.moduleMemberId}
        interviewModuleId={sidePanelData?.data.interviewModuleId}
      />
    );
  } else if (sidePanelData.data?.type === SidePanelType.AIAssistant) {
    jsx = <AIAssistantSidepanel />;
  } else if (sidePanelData.data?.type === SidePanelType.AiScheduling) {
    jsx = (
      <AiSchedulingSidepanel
        taskId={sidePanelData.data.taskId}
        orgId={sidePanelData.data.orgId}
        actorEmployeeId={sidePanelData.data.actorEmployeeId}
        applicationId={sidePanelData.data.applicationId}
      />
    );
  }

  return (
    <Drawer open={Boolean(sidePanelData.data)} onClose={hideSidePanelClose} width={drawerWidth}>
      {jsx}
      {sidePanelState.showUnsavedChangesDialog && sidePanelState.unsavedChangesConfig.notes && (
        <DiscardNoteConfirmationDialog
          onClose={() => {
            SidePanelManager.setShowUnsavedChangesDialog(false);
          }}
          onDiscard={() => {
            SidePanelManager.setHasUnsavedNoteChanges(false);
            SidePanelManager.setShowUnsavedChangesDialog(false);
            SidePanelManager.closeSidePanel();
          }}
        />
      )}
    </Drawer>
  );
};

const SidePanel = () => {
  return (
    <SidePanelProvider>
      <SidePanelBase />
    </SidePanelProvider>
  );
};

export default SidePanel;
